import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface CreateFinancialAdjustmentRequest {
  expenseBudgetDelta: number;
  customerChargeDelta: number;
  companyAdvanceAmount: number;
  reason: string;
}

export interface FinancialAdjustmentItem {
  id: string;
  adjustmentNo: string;
  targetType: 'BOOKING' | 'TOUR';
  bookingId: string | null;
  tourId: string | null;
  tourPriceDelta: number | string;
  expenseBudgetDelta: number | string;
  customerChargeDelta: number | string;
  companyAdvanceAmount: number | string;
  advanceAttachmentPath: string | null;
  advanceAttachmentOriginalName: string | null;
  reason: string;
  status:
    | 'DRAFT'
    | 'SUPERVISOR_REVIEW'
    | 'APPROVED'
    | 'REJECTED'
    | 'CANCELLED'
    | 'DELETED';
  requestedById: string;
  requestedBy: { firstName: string; lastName: string };
  createdAt: string;
  updatedAt: string;
}

export interface PendingFinancialAdjustmentItem extends FinancialAdjustmentItem {
  booking: {
    bookingNo: string;
    bookingType: 'JOIN_TOUR' | 'GROUP_TOUR';
    tourName: string;
    departureDate: string;
    pax: number;
    totalAmount: number | string;
    estimatedExpense: number | string;
    booking_customer: { name: string };
  } | null;
  tour: {
    tourNo: string;
    tourCode: string;
    tourName: string;
    country: string;
    travelDate: string;
    returnDate: string | null;
    tourprice: number | string;
    tourTotalAmount: number | string;
    requiredPax: number | null;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class FinancialAdjustmentService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/booking-financial-adjustments`;

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }

  async getByBookingId(bookingId: string): Promise<FinancialAdjustmentItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ adjustments: FinancialAdjustmentItem[] }>(
        `${this.apiUrl}/booking/${bookingId}`,
        { withCredentials: true },
      ),
    );
    return response.adjustments;
  }

  async getPending(): Promise<PendingFinancialAdjustmentItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ adjustments: PendingFinancialAdjustmentItem[] }>(
        `${this.apiUrl}/pending`,
        { withCredentials: true },
      ),
    );
    return response.adjustments;
  }

  async create(
    bookingId: string,
    input: CreateFinancialAdjustmentRequest,
    attachment: File | null,
  ): Promise<FinancialAdjustmentItem> {
    const formData = new FormData();
    formData.append('expenseBudgetDelta', String(input.expenseBudgetDelta));
    formData.append('customerChargeDelta', String(input.customerChargeDelta));
    formData.append('companyAdvanceAmount', String(input.companyAdvanceAmount));
    formData.append('reason', input.reason);
    if (attachment) formData.append('advanceAttachment', attachment);

    const response = await firstValueFrom(
      this.http.post<{ message: string; adjustment: FinancialAdjustmentItem }>(
        `${this.apiUrl}/${bookingId}`,
        formData,
        { withCredentials: true },
      ),
    );
    return response.adjustment;
  }

  async approve(adjustmentId: string): Promise<FinancialAdjustmentItem> {
    const response = await firstValueFrom(
      this.http.patch<{ message: string; adjustment: FinancialAdjustmentItem }>(
        `${this.apiUrl}/${adjustmentId}/approve`,
        {},
        { withCredentials: true },
      ),
    );
    return response.adjustment;
  }

  async reject(adjustmentId: string): Promise<FinancialAdjustmentItem> {
    const response = await firstValueFrom(
      this.http.patch<{ message: string; adjustment: FinancialAdjustmentItem }>(
        `${this.apiUrl}/${adjustmentId}/reject`,
        {},
        { withCredentials: true },
      ),
    );
    return response.adjustment;
  }
}
